/**
 * First-run builder onboarding — pure gating + step logic.
 *
 * Kept framework-free so the "show it once, never on share/load" contract
 * is unit-testable without mounting the 3D builder. The React shell
 * (BuilderIntro.tsx) only wires refs, focus, and localStorage to these.
 */

/** localStorage key — follows the existing `bb-*` builder flag convention. */
export const BUILDER_INTRO_SEEN_KEY = "bb-builder-intro-seen";

/**
 * The viewer-help panel auto-opens on first visit off its own
 * `bb-viewer-help-seen` flag. The intro's canvas coach-mark already
 * teaches the same 3D interaction, so when the intro is going to show we
 * pre-mark viewer-help as seen — that way only ONE overlay auto-opens
 * (the intro), per "intro first, viewer-help only after". Returning
 * users (intro already seen) keep the unchanged viewer-help behaviour.
 */
export const VIEWER_HELP_SEEN_KEY = "bb-viewer-help-seen";

export type BuilderIntroStep = "entry" | "canvas" | "tabs";

/** Ordered walk of the coach-mark sequence after the entry choice. */
export const BUILDER_INTRO_STEPS: readonly BuilderIntroStep[] = [
	"entry",
	"canvas",
	"tabs",
];

export interface ShouldShowIntroInput {
	hasSeedDimensions?: boolean;
	/** A saved design is loaded (`?id=`), so this isn't a cold first visit. */
	hasLoadedDesign: boolean;
	/** A share config is present (`?share=`), so the user arrived via a link. */
	hasShareConfig: boolean;
	/** Whether the `bb-builder-intro-seen` flag is already set. */
	introSeen: boolean;
}

/**
 * Show the intro only on a genuine cold first visit to `/builder/:type`:
 * no loaded design, no share config, and not seen before. Share/load
 * flows skip it entirely.
 */
export function shouldShowBuilderIntro({
	hasLoadedDesign,
	hasSeedDimensions = false,
	hasShareConfig,
	introSeen,
}: ShouldShowIntroInput): boolean {
	if (hasLoadedDesign || hasShareConfig || hasSeedDimensions) return false;
	return !introSeen;
}

/** Next step in the sequence, or null when the intro is complete. */
export function nextIntroStep(
	current: BuilderIntroStep,
): BuilderIntroStep | null {
	const index = BUILDER_INTRO_STEPS.indexOf(current);
	if (index < 0 || index >= BUILDER_INTRO_STEPS.length - 1) return null;
	return BUILDER_INTRO_STEPS[index + 1];
}

/** 1-based position of a step (for "Step {current} of {total}"). */
export function introStepPosition(current: BuilderIntroStep): {
	current: number;
	total: number;
} {
	return {
		current: BUILDER_INTRO_STEPS.indexOf(current) + 1,
		total: BUILDER_INTRO_STEPS.length,
	};
}

/** Safe localStorage read — returns false if storage is unavailable. */
export function readIntroSeen(): boolean {
	try {
		return window.localStorage.getItem(BUILDER_INTRO_SEEN_KEY) !== null;
	} catch {
		return false;
	}
}

/** Persist the seen flag; swallows storage errors (private mode, quota). */
export function markIntroSeen(): void {
	try {
		window.localStorage.setItem(BUILDER_INTRO_SEEN_KEY, "1");
	} catch {
		/* ignore */
	}
}

/** Pre-seed viewer-help-seen so it doesn't auto-open behind the intro. */
export function suppressViewerHelpAutoOpen(): void {
	try {
		window.localStorage.setItem(VIEWER_HELP_SEEN_KEY, "1");
	} catch {
		/* ignore */
	}
}
